import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Input, notification } from 'antd';
import { useDebounce } from 'util/Hooks';
import { fetchPackageWithExactExtCode } from 'constants/operations';
import Card from 'components/card';
import PackageSelectModal from 'components/packageSelectModal';
import { useAssignShipmentDriver } from 'services/journeys';

const { Search } = Input;
const WAIT_INPUT_EXTERNAL_CODE = 600;

// Asigna un envío a este driver por código externo: buscar por código, y si hay
// varios matches elegir en modal.
const AssignPackage = ({ paquerId, onAssigned }) => {
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const debouncedSearch = useDebounce(search, WAIT_INPUT_EXTERNAL_CODE);
  const { mutateAsync: assignShipment } = useAssignShipmentDriver();

  const closeModal = () => {
    setModalOpen(false);
    setOptions([]);
  };

  const assign = async (packet) => {
    setLoading(true);
    try {
      await assignShipment({
        shipmentId: packet.id,
        driverId: Number(paquerId),
      });
      notification.success({
        message: 'Paquete asignado',
        description: `El paquete ${packet.externalCode} fue asignado al driver.`,
      });
      closeModal();
      setSearch('');
      onAssigned();
    } catch {
      // el hook ya notifica el error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const run = async (code) => {
      setLoading(true);
      const packets = await fetchPackageWithExactExtCode(code);
      if (packets instanceof Error) {
        setLoading(false);
        notification.error({
          message: 'Paquete no encontrado',
          description: packets.message,
        });
        return;
      }
      if (packets.length === 1) {
        assign(packets[0]);
        return;
      }
      // varios matches con el mismo código externo: que el usuario elija
      setOptions(
        packets.map((p) => ({
          ...p,
          destinationAddress:
            p.shippingScheduleDestination?.shippingAddress?.addressDetail,
        })),
      );
      setModalOpen(true);
      setLoading(false);
    };
    if (debouncedSearch.trim()) run(debouncedSearch.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  return (
    <>
      <PackageSelectModal
        visible={modalOpen}
        packages={options}
        handleCloseModal={closeModal}
        handlePackageSelection={assign}
        disabled={{ marketplace: true, statusDescription: true }}
      />
      <Card title="Asignar envío al driver" style={{ marginBottom: 16 }}>
        <Search
          style={{ maxWidth: 400 }}
          value={search}
          placeholder="Código externo"
          loading={loading}
          onChange={(e) => setSearch(e.target.value.trim())}
        />
      </Card>
    </>
  );
};

AssignPackage.propTypes = {
  paquerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onAssigned: PropTypes.func.isRequired,
};

export default AssignPackage;
